import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '../ui/Card';
import apiClient from '../../utils/api';

// The coach speaks as a short, prioritised focus list: a headline takeaway plus
// 2-4 things to work on, most important first. Each item is a glanceable title
// that expands to the "why". The backend sends this as a JSON string in
// insight_text; older rows may still be a plain paragraph, which we render as-is.
const parseInsight = (text) => {
  if (!text) return null;
  try {
    const obj = JSON.parse(text);
    if (obj && Array.isArray(obj.focus) && obj.focus.length) {
      return { headline: obj.headline || '', focus: obj.focus };
    }
  } catch (_) {
    // legacy plain-paragraph insight
  }
  return { headline: '', focus: null, paragraph: text };
};

const CoachIcon = () => (
  <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
  </svg>
);

// Priority is assigned by the coach per item (high/medium/low), shown as a
// colored rank badge + label. Older cached insights have no priority field —
// fall back to rank order (1st = high, 2nd = medium, rest = low).
const PRIORITY = {
  high: { label: 'High priority', badge: 'bg-primary-500 text-[#1a1200]', text: 'text-primary-700 dark:text-primary-300' },
  medium: { label: 'Medium priority', badge: 'bg-amber-500 text-[#241a00]', text: 'text-amber-700 dark:text-amber-300' },
  low: { label: 'Lower priority', badge: 'bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-100', text: 'text-gray-500 dark:text-gray-400' },
};
const RANK_FALLBACK = ['high', 'medium', 'low'];

const FocusItem = ({ item, index }) => {
  const [open, setOpen] = useState(false);
  const hasDetail = Boolean(item.detail);
  const key = PRIORITY[item.priority] ? item.priority : RANK_FALLBACK[Math.min(index, RANK_FALLBACK.length - 1)];
  const p = PRIORITY[key];
  return (
    <div className="border-b border-surface-border dark:border-surface-dark-border last:border-b-0">
      <button
        type="button"
        onClick={() => hasDetail && setOpen((o) => !o)}
        aria-expanded={hasDetail ? open : undefined}
        className={`w-full flex items-start gap-3 py-3 text-left ${hasDetail ? '' : 'cursor-default'}`}
      >
        <span className={`mt-0.5 shrink-0 flex items-center justify-center w-6 h-6 rounded-full text-sm font-bold ${p.badge}`}>
          {index + 1}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-base font-semibold text-gray-900 dark:text-white">
            {item.title}
          </span>
          <span className={`block text-sm font-medium ${p.text}`}>{p.label}</span>
        </span>
        {hasDetail && (
          <svg
            className={`mt-1 w-4 h-4 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </button>
      {hasDetail && open && (
        <p className="pl-9 pr-2 pb-3 -mt-1 text-base text-gray-700 dark:text-gray-200 leading-relaxed">
          {item.detail}
        </p>
      )}
    </div>
  );
};

const CoachingInsightCard = () => {
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchInsight = async () => {
      try {
        const tzOffset = Math.round(-new Date().getTimezoneOffset() / 60);
        const res = await apiClient.get(`/api/coaching/insight?tz_offset=${tzOffset}`);
        setInsight(res.data);
      } catch (err) {
        console.error('Failed to load coaching insight:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchInsight();
  }, []);

  const parsed = useMemo(() => parseInsight(insight?.insight_text), [insight]);

  // A concrete "when" — relative for the last hour (it genuinely is fresh then),
  // otherwise an absolute clock time / date so "just now" never lingers on a
  // hours-old insight. Kept short.
  const updatedLabel = (isoString) => {
    const then = new Date(isoString);
    const now = new Date();
    const mins = Math.floor((now.getTime() - then.getTime()) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const sameDay = then.toDateString() === now.toDateString();
    const time = then.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    if (sameDay) return `today ${time}`;
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (then.toDateString() === yesterday.toDateString()) return `yesterday ${time}`;
    return `${then.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${time}`;
  };

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-base text-gray-600 dark:text-gray-300">
            Unable to load coaching insights right now.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-l-4 border-l-primary-500">
      <CardContent className="p-6">
        {loading ? (
          <div className="animate-pulse space-y-4">
            <div className="flex items-center gap-2">
              <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-28" />
            </div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6" />
            <div className="space-y-3 pt-1">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="h-6 w-6 bg-gray-200 dark:bg-gray-700 rounded-full shrink-0" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <CoachIcon />
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Health Coach</h3>
              </div>
              {insight?.generated_at && (
                <span className="text-sm text-gray-500 dark:text-gray-400 shrink-0">
                  Updated {updatedLabel(insight.generated_at)}
                </span>
              )}
            </div>

            {parsed?.focus ? (
              <div>
                {parsed.focus.map((item, i) => (
                  <FocusItem key={i} item={item} index={i} />
                ))}
              </div>
            ) : (
              <p className="text-base text-gray-700 dark:text-gray-200 leading-relaxed">
                {parsed?.paragraph}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CoachingInsightCard;
